import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Receipt } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { authClient } from '@/lib/auth-client'
import { type RouterOutputs, trpc } from '@/trpc/client'

export function InvitePage() {
	const params = useParams<{ token: string }>()
	const token = params.token ?? ''

	const preview = trpc.organizations.previewInvitation.useQuery(
		{ token },
		{ enabled: token.length > 0, retry: false },
	)

	return (
		<div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-12">
			<div className="mb-6 flex justify-center">
				<Link to="/" className="flex items-center gap-2 font-semibold">
					<div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
						<Receipt className="size-4" />
					</div>
					Warrantly
				</Link>
			</div>
			<Card>
				<CardContent className="p-6">
					{preview.isPending ? (
						<div className="space-y-3">
							<Skeleton className="h-6 w-2/3" />
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : preview.isError ? (
						<Alert variant="destructive">
							<AlertCircle />
							<AlertTitle>Couldn't load the invitation</AlertTitle>
							<AlertDescription>
								{preview.error?.message ?? 'Unknown error'}
							</AlertDescription>
						</Alert>
					) : (
						<InviteState token={token} preview={preview.data} />
					)}
				</CardContent>
			</Card>
		</div>
	)
}

type Preview = RouterOutputs['organizations']['previewInvitation']

function InviteState({ token, preview }: { token: string; preview: Preview }) {
	if (!preview.found) {
		return (
			<Empty
				title="This invitation doesn't exist"
				body="The link may have been mistyped. Ask whoever sent it for a fresh one."
			/>
		)
	}
	if (preview.expired || preview.status === 'canceled') {
		return (
			<Empty
				title="This invitation is no longer valid"
				body={
					preview.expired
						? 'It expired. Ask the inviter to send a new one.'
						: 'It was canceled by the inviter.'
				}
			/>
		)
	}
	if (preview.status === 'accepted' || preview.status === 'rejected') {
		return (
			<Empty
				title={
					preview.status === 'accepted'
						? 'You already accepted this invitation'
						: 'This invitation was declined'
				}
				body="If you think this is wrong, contact the inviter."
			/>
		)
	}

	if (!preview.signedInEmail) {
		return <SignInPrompt token={token} preview={preview} />
	}
	if (!preview.emailMatchesSession) {
		return <EmailMismatch preview={preview} />
	}
	return <AcceptPrompt token={token} preview={preview} />
}

function Empty({ title, body }: { title: string; body: string }) {
	return (
		<div className="space-y-3 text-center">
			<h1 className="text-base font-semibold">{title}</h1>
			<p className="text-sm text-muted-foreground">{body}</p>
			<Button asChild variant="outline" size="sm">
				<Link to="/">Back to Warrantly</Link>
			</Button>
		</div>
	)
}

function SignInPrompt({ token, preview }: { token: string; preview: Preview }) {
	const next = `/invite/${token}`
	return (
		<div className="space-y-3">
			<h1 className="text-base font-semibold">
				You've been invited to {preview.organizationName}
			</h1>
			<p className="text-sm text-muted-foreground">
				{preview.inviterName ?? 'Someone'} ({preview.inviterEmail}) invited you
				to collaborate. Sign in or sign up with{' '}
				<span className="font-medium">{preview.email}</span> to accept.
			</p>
			<div className="grid gap-2 pt-1">
				<Button asChild>
					<Link to={`/login?next=${encodeURIComponent(next)}`}>Sign in</Link>
				</Button>
				<Button asChild variant="outline">
					<Link to={`/register?next=${encodeURIComponent(next)}`}>
						Create an account
					</Link>
				</Button>
			</div>
		</div>
	)
}

function EmailMismatch({ preview }: { preview: Preview }) {
	async function signOut() {
		await authClient.signOut()
		window.location.reload()
	}
	return (
		<div className="space-y-3">
			<h1 className="text-base font-semibold">Different email signed in</h1>
			<p className="text-sm text-muted-foreground">
				This invitation was sent to{' '}
				<span className="font-medium">{preview.email}</span>, but you're signed
				in as <span className="font-medium">{preview.signedInEmail}</span>.
			</p>
			<div className="flex gap-2 pt-1">
				<Button variant="outline" onClick={signOut}>
					Sign out
				</Button>
				<Button asChild variant="ghost">
					<Link to="/app">Back to my app</Link>
				</Button>
			</div>
		</div>
	)
}

function AcceptPrompt({ token, preview }: { token: string; preview: Preview }) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const accept = trpc.organizations.acceptInvitation.useMutation({
		onSuccess: async () => {
			await queryClient.invalidateQueries()
			toast.success(`Joined ${preview.organizationName}`)
			navigate('/app')
		},
		onError: (err) => {
			toast.error(err.message || 'Could not accept the invitation.')
		},
	})

	const reject = trpc.organizations.rejectInvitation.useMutation({
		onSuccess: () => {
			toast.message('Declined.')
			navigate('/app')
		},
		onError: (err) => toast.error(err.message || 'Could not decline.'),
	})

	const pending = accept.isPending || reject.isPending

	return (
		<div className="space-y-3">
			<h1 className="text-base font-semibold">
				Join {preview.organizationName}?
			</h1>
			<p className="text-sm text-muted-foreground">
				{preview.inviterName ?? 'Someone'} ({preview.inviterEmail}) invited you
				to collaborate.
			</p>
			<div className="flex gap-2 pt-2">
				<Button onClick={() => accept.mutate({ token })} disabled={pending}>
					{accept.isPending ? 'Joining…' : 'Accept'}
				</Button>
				<Button
					variant="outline"
					onClick={() => reject.mutate({ token })}
					disabled={pending}
				>
					{reject.isPending ? 'Declining…' : 'Decline'}
				</Button>
			</div>
		</div>
	)
}

// Used by the sign-in / sign-up screens to honour ?next=/invite/...
export function useNextOrDefault(defaultPath = '/app') {
	const url = new URL(window.location.href)
	const next = url.searchParams.get('next')
	return next && next.startsWith('/') ? next : defaultPath
}
